/**
 * Genera link Bold único y persiste transaction_id en el pedido (flujo create con payment_method bold).
 */
import { createBoldPaymentLink, getBoldLinkDetails } from './boldService'
import { updateOrderStatus } from '../orders/updateOrderStatusService'

const BOLD_LINK_DEBOUNCE_MS = 2500

export type BoldSingleLinkFailureStep = 'bold_link' | 'bold_patch'

export interface BoldPatchRetryContext {
  transactionId: string
  status: string
  paymentMethod: string
}

export async function generateAndSaveSingleBoldLinkForOrder(params: {
  orderId: number
  totalAmount: number
  storeName: string
  status: string
  paymentMethod: string
  onProgress?: (message: string) => void
}): Promise<{
  ok: boolean
  url?: string
  error?: string
  failureStep?: BoldSingleLinkFailureStep
  patchRetryContext?: BoldPatchRetryContext
}> {
  const { orderId, totalAmount, storeName, status, paymentMethod, onProgress } = params

  onProgress?.('Generando link de pago Bold...')
  const reference = 'ped~' + orderId
  const description = 'Pago pedido ' + orderId

  const linkResult = await createBoldPaymentLink({
    totalAmount,
    reference,
    description,
    store_name: storeName
  })

  if (!linkResult.success || !linkResult.payment_link) {
    return {
      ok: false,
      error: linkResult.error || 'No se pudo generar el link de pago Bold',
      failureStep: 'bold_link'
    }
  }

  onProgress?.('Obteniendo estado del link...')
  await new Promise((resolve) => setTimeout(resolve, BOLD_LINK_DEBOUNCE_MS))

  const linkDetails = await getBoldLinkDetails(linkResult.payment_link, storeName)
  const transactionIdToSave =
    linkDetails.success && linkDetails.transaction_id
      ? linkDetails.transaction_id
      : linkResult.payment_link

  const patchContext: BoldPatchRetryContext = {
    transactionId: transactionIdToSave,
    status: status || 'processing',
    paymentMethod: paymentMethod || 'bold'
  }

  try {
    await updateOrderStatus(orderId, {
      status: patchContext.status,
      meta_data: [
        { key: 'transaction_id', value: transactionIdToSave },
        { key: 'payment_method', value: patchContext.paymentMethod }
      ]
    })
  } catch (patchErr: unknown) {
    const patchMessage =
      patchErr && typeof patchErr === 'object' && 'response' in patchErr
        ? String(
            (patchErr as { response?: { data?: { message?: string; error?: string } } }).response
              ?.data?.message ||
              (patchErr as { response?: { data?: { error?: string } } }).response?.data?.error ||
              ''
          )
        : 'El enlace se generó pero no se pudo confirmar en el sistema.'
    return {
      ok: false,
      error: patchMessage || 'El enlace se generó pero no se pudo confirmar en el sistema.',
      failureStep: 'bold_patch',
      url: linkResult.url || linkResult.payment_link,
      patchRetryContext: patchContext
    }
  }

  return { ok: true, url: linkResult.url || linkResult.payment_link }
}

/** Reintento solo del PATCH cuando el link Bold ya se generó */
export async function retrySaveBoldTransactionOnOrder(
  orderId: number,
  ctx: BoldPatchRetryContext
): Promise<{ ok: boolean; error?: string }> {
  try {
    await updateOrderStatus(orderId, {
      status: ctx.status,
      meta_data: [
        { key: 'transaction_id', value: ctx.transactionId },
        { key: 'payment_method', value: ctx.paymentMethod }
      ]
    })
    return { ok: true }
  } catch (patchErr: unknown) {
    const patchMessage =
      patchErr && typeof patchErr === 'object' && 'response' in patchErr
        ? String(
            (patchErr as { response?: { data?: { message?: string; error?: string } } }).response
              ?.data?.message ||
              (patchErr as { response?: { data?: { error?: string } } }).response?.data?.error ||
              ''
          )
        : 'No se pudo confirmar el enlace de pago en el sistema.'
    return { ok: false, error: patchMessage }
  }
}
